import type { ForemanJobDetail } from '@ups/xplat/api/dto';

/**
 * Foreman Toolbox job-list visibility (US-30867).
 *
 * A dispatched job that has had no time keyed AND no live dispatch in the
 * trailing window is "stale" and hidden by default to cut list clutter. Hidden
 * jobs stay findable through the existing global job typeahead, and reappear on
 * their own once new time is keyed or a new dispatch lands (recency crosses back
 * inside the window on the next refresh).
 *
 * Recency is derived from nested records because ForemanJobDetail carries no
 * top-level "last keyed" or "dispatch date" field:
 *  - keyed activity  = latest labor `modifiedDate` / `localChangedAtUtcMs`
 *                      (the keyed/edited timestamp) plus timesheet `modifiedDate`
 *  - dispatch activity = latest dispatch-row `modifiedDate` across LIVE dispatches
 *                      (the assignment/change audit instant). Scheduled start/end
 *                      dates are deliberately NOT used: they are often future or
 *                      open-ended, which would keep every job visible forever
 *                      (feature no-op). `modifiedDate` is a real event instant.
 *
 * All logic is pure and shared so web (foreman-individual-time-entry-page) and
 * mobile (page-job-selection) apply the identical rule.
 */
export const FOREMAN_JOB_STALE_WINDOW_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Coerce a Date | ISO string | epoch-ms number to epoch ms, or null when unusable. */
function toEpochMs(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

/** Largest of the candidate instants, ignoring nulls; null when none are usable. */
function maxMs(candidates: (number | null)[]): number | null {
  let max: number | null = null;
  for (const candidate of candidates) {
    if (candidate != null && (max == null || candidate > max)) {
      max = candidate;
    }
  }
  return max;
}

/**
 * Latest time-keying instant on the job: the max labor `modifiedDate` /
 * `localChangedAtUtcMs` across every employee dispatch, plus timesheet
 * `modifiedDate`. Returns null when no time has ever been keyed.
 */
export function getMostRecentKeyedActivityMs(
  job: Partial<ForemanJobDetail>
): number | null {
  const candidates: (number | null)[] = [];

  for (const employee of job.employees ?? []) {
    for (const labor of employee.labors ?? []) {
      candidates.push(toEpochMs(labor.modifiedDate));
      candidates.push(toEpochMs(labor.localChangedAtUtcMs));
    }
  }

  for (const timeSheet of job.timeSheets ?? []) {
    candidates.push(toEpochMs(timeSheet.modifiedDate));
  }

  return maxMs(candidates);
}

/**
 * Latest dispatch instant on the job across LIVE (bActive !== false) employee
 * and equipment dispatches, using each dispatch row's `modifiedDate` — the
 * server audit instant the assignment was created or last changed. Scheduled
 * start/end/last-day dates are intentionally excluded: they can be future or
 * open-ended and would make the hide rule a no-op. Inactive (rolled-off)
 * dispatch rows do not contribute. Returns null when there is no live dispatch.
 */
export function getMostRecentDispatchActivityMs(
  job: Partial<ForemanJobDetail>
): number | null {
  const candidates: (number | null)[] = [];

  for (const employee of job.employees ?? []) {
    if (employee.bActive === false) {
      continue;
    }
    candidates.push(toEpochMs(employee.modifiedDate));
  }

  for (const equipment of job.equipment ?? []) {
    if (equipment.bActive === false) {
      continue;
    }
    candidates.push(toEpochMs(equipment.modifiedDate));
  }

  return maxMs(candidates);
}

/**
 * Whether the job has ever been dispatched (any employee or equipment row,
 * active or not). A never-dispatched job - e.g. one just created - is not "a
 * dispatched job" and is never subject to the hide rule.
 */
export function hasForemanJobDispatchHistory(
  job: Partial<ForemanJobDetail>
): boolean {
  return (job.employees?.length ?? 0) > 0 || (job.equipment?.length ?? 0) > 0;
}

/**
 * Whether the job currently has at least one LIVE (bActive !== false) employee
 * or equipment dispatch row, regardless of whether that row carries a readable
 * `modifiedDate`.
 *
 * This is deliberately distinct from `hasForemanJobDispatchHistory` (which
 * counts rolled-off rows too) and from `getMostRecentDispatchActivityMs`
 * (which needs a parseable instant). It is the difference between "this job's
 * crew has gone" and "this job has a crew but we cannot read when it changed",
 * and only the first of those is the clutter this feature targets. See the
 * unknown-vs-old note in `isForemanJobStale` (WI 31896).
 */
export function hasLiveForemanJobDispatch(
  job: Partial<ForemanJobDetail>
): boolean {
  const liveEmployee = (job.employees ?? []).some((e) => e.bActive !== false);
  const liveEquipment = (job.equipment ?? []).some((e) => e.bActive !== false);
  return liveEmployee || liveEquipment;
}

/**
 * A dispatched job is stale (hidden by default) when neither its latest keyed
 * time nor its latest live dispatch falls within the trailing window. The window
 * boundary is inclusive: activity exactly `windowDays` ago still counts as recent.
 */
export function isForemanJobStale(
  job: Partial<ForemanJobDetail>,
  nowMs: number,
  windowDays: number = FOREMAN_JOB_STALE_WINDOW_DAYS
): boolean {
  if (!hasForemanJobDispatchHistory(job)) {
    return false;
  }

  const cutoff = nowMs - windowDays * DAY_MS;
  const keyed = getMostRecentKeyedActivityMs(job);
  const dispatch = getMostRecentDispatchActivityMs(job);

  // WI 31896 -- UNKNOWN IS NOT OLD, so this arm fails OPEN.
  //
  // Every recency instant here is derived from a nested `modifiedDate`, and
  // `toEpochMs` yields null for a value that is absent, malformed, or in a
  // shape Date.parse rejects. Without this guard a job with a LIVE crew whose
  // dispatch rows carry no readable timestamp scored as "no recent activity"
  // and was hidden, no matter how recent the real dispatch was -- a
  // serialization regression upstream would silently empty the job list. The
  // DTO comments about emitting a trailing Z (ForemanEquipmentDispatchDto,
  // ForemanTimeSheetDto) show that shape has already had to be chased once.
  //
  // Fail-open is the right direction because the costs are asymmetric: an
  // over-shown job is clutter, which is cosmetic and self-correcting, while an
  // over-hidden job is a foreman who cannot reach a job to key time on it,
  // which stops work. It is also self-reinforcing, because not keying time is
  // what keeps a job stale.
  //
  // The `hasLiveForemanJobDispatch` term is what keeps this from swallowing
  // the feature: a genuinely rolled-off job (all dispatch rows inactive, no
  // time ever keyed) also produces two nulls, and that job MUST still hide --
  // it is precisely the clutter AC1 targets. Live crew plus unreadable dates
  // is the only case treated as unknown.
  if (keyed == null && dispatch == null && hasLiveForemanJobDispatch(job)) {
    return false;
  }

  const keyedRecent = keyed != null && keyed >= cutoff;
  const dispatchRecent = dispatch != null && dispatch >= cutoff;

  return !keyedRecent && !dispatchRecent;
}

/**
 * Options for the default-view visibility decision.
 */
export interface ForemanJobVisibilityOptions {
  /** Trailing staleness window; defaults to {@link FOREMAN_JOB_STALE_WINDOW_DAYS}. */
  windowDays?: number;
  /**
   * WI 31896: `foremanJobID`s the user deliberately brought back into the list
   * (mobile: picked from the job typeahead; web: added through Add Job). These
   * are never hidden, so a list rebuild cannot silently undo an explicit user
   * action.
   */
  exemptJobIds?: ReadonlySet<string> | null;
}

/**
 * Whether a job should be hidden from the default (no-search) list: stale AND
 * not user-exempted.
 *
 * WI 31896: the exemption lives HERE rather than at each call site so both
 * platforms share one predicate, which is what this module's contract already
 * promised ("All logic is pure and shared so web and mobile apply the identical
 * rule"). It did not hold: web kept an exemption set in its own component while
 * mobile had none, so on mobile a job re-added from the typeahead was hidden
 * again by the next list rebuild -- and being unreachable, no time could be
 * keyed on it, which kept it stale. One predicate, one rule.
 */
export function isForemanJobHidden(
  job: Partial<ForemanJobDetail>,
  nowMs: number,
  options?: ForemanJobVisibilityOptions
): boolean {
  const exemptIds = options?.exemptJobIds;
  if (exemptIds && job.foremanJobID && exemptIds.has(job.foremanJobID)) {
    return false;
  }
  return isForemanJobStale(job, nowMs, options?.windowDays);
}

/**
 * The default (no-search) visible subset of a foreman job list: every job except
 * those that are stale. Non-job rows (e.g. the mobile "Create Job" action) have
 * no dispatch history and are always kept.
 */
export function filterVisibleForemanJobs<T extends Partial<ForemanJobDetail>>(
  jobs: T[],
  nowMs: number,
  options?: ForemanJobVisibilityOptions
): T[] {
  return jobs.filter((job) => !isForemanJobHidden(job, nowMs, options));
}
